import React, { useEffect } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Button, Typography, Alert } from '@mui/material';
import CreateAlbumWizardPanels from './CreateAlbumWizardPanels';
import { useCreateAlbumWizard } from './useCreateAlbumWizard';
import type { CreateAlbumFormPayload } from './createAlbumWizardTypes';
import type { SerializedCreateAlbumWizardState } from './createAlbumWizardTypes';

interface FirstAlbumAlbumStepProps {
   initial?: Partial<SerializedCreateAlbumWizardState>;
   onComplete: (payload: CreateAlbumFormPayload) => void;
   onBackToMarketing: () => void;
   /** Called whenever wizard state changes (for sessionStorage) */
   onStateChange?: (state: SerializedCreateAlbumWizardState) => void;
}

const FirstAlbumAlbumStep: React.FC<FirstAlbumAlbumStepProps> = ({
   initial,
   onComplete,
   onBackToMarketing,
   onStateChange,
}) => {
   const theme = useTheme();
   const primaryDark = theme.palette.primary.dark;
   const wizard = useCreateAlbumWizard({ initial });
   const {
      activeStep,
      handleNext,
      handleBack,
      buildPayload,
      setError,
      error,
      stepSubtitle,
      serialize,
   } = wizard;

   useEffect(() => {
      onStateChange?.(serialize());
   }, [serialize, onStateChange]);

   const handlePrimary = () => {
      if (activeStep < 2) {
         handleNext();
         return;
      }

      const { payload, error: buildErr } = buildPayload();
      if (buildErr || !payload) {
         setError(buildErr || 'Invalid album data');
         return;
      }
      onComplete(payload);
   };

   const primaryLabel =
      activeStep < 2 ? 'Next' : 'Continue to account';

   return (
      <Box>
         <Typography
            variant='h5'
            sx={{
               fontFamily: theme.typography.h3.fontFamily,
               fontWeight: 600,
               color: 'text.primary',
               letterSpacing: theme.typography.h3.letterSpacing,
               mb: 0.5,
            }}
         >
            Design your first album
         </Typography>
         <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Step {activeStep + 1} of 3 • {stepSubtitle}
         </Typography>

         {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
               {error}
            </Alert>
         )}

         <CreateAlbumWizardPanels wizard={wizard} />

         <Box
            sx={{
               display: 'flex',
               flexWrap: 'wrap',
               gap: 2,
               justifyContent: 'flex-end',
               mt: 3,
            }}
         >
            <Button
               onClick={activeStep === 0 ? onBackToMarketing : handleBack}
               sx={{
                  color: 'text.secondary',
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': {
                     bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                     color: 'primary.main',
                  },
               }}
            >
               {activeStep === 0 ? 'Back to intro' : 'Back'}
            </Button>
            <Button
               variant='contained'
               color='primary'
               onClick={handlePrimary}
               sx={{
                  fontWeight: 600,
                  px: 3,
                  textTransform: 'none',
                  boxShadow: (t) => `0 4px 14px ${alpha(t.palette.primary.main, 0.35)}`,
                  '&:hover': {
                     bgcolor: primaryDark,
                     boxShadow: (t) => `0 6px 20px ${alpha(t.palette.primary.main, 0.45)}`,
                  },
               }}
            >
               {primaryLabel}
            </Button>
         </Box>
      </Box>
   );
};

export default FirstAlbumAlbumStep;
